import { themeColors } from "../themeColors";
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\pages\AllIntroductions.js
 * @brief  This file is responsible for handlingall introductions page.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import _ from "lodash";
import ViewIntroductionModal from "../components/Introduction/ViewIntroductionModal";
import { getLocalStorage, setLocalStorage } from "../helpers/common";
import {
  WANT_AD_KEY,
  INTRODUCTION_NOT_A_FIT_FROM_PENDING_LIST,
  INTRODUCTION_NOT_A_FIT_FROM_ACCEPTED_LIST,
} from "../constants/common";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import {
  getIntroductions,
  getAcceptedIntroductions,
  getIntroductionDetaisById,
  updateIntroNotFit,
} from "../services/introductionService";
import AcceptedIntroductionList from "../components/Introduction/AcceptedIntroductionList";
import PendingIntroductionList from "../components/Introduction/PendingIntroductionList";
import { toast } from "react-toastify";
import { updateIntroduction } from "../helpers/message";
import MarkFitModal from "../components/Introduction/MarkFitModal";
import { PageWrapper } from "@/components/PageWrapper";
import useLoading from "@/hooks/useLoading";

const PENDING_INTROS_KEY = "allIntroductionsPending";
const ACCEPTED_INTROS_KEY = "allIntroductionsAccepted";
const INTRO_DETAILS_KEY = "allIntroductionsDetails";
const INTRO_LIST_PAGE_LIMIT = 4;

const AllIntroductions = () => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startLoading, stopLoading } = useLoading();
  const [introductionId, setIntroId] = useState("");
  const [showMarkFitModal, setMarkFitModal] = useState(false);
  const markFitModalClose = () => setMarkFitModal(false);
  const [markNotFitStatus, setMarkNotFitStatus] = useState([]);

  const [showNotFirReasnPopup, setNotFitReasonPopup] = useState(false);

  const markFitReasonModalClose = () => setNotFitReasonPopup(false);

  const [selectedIntroId, setSelectedIntroId] = useState(null);
  const [showViewIntroductionModal, setViewIntroductionModal] = useState(false);
  const handleIntroDetailModal = (modalState) =>
    setViewIntroductionModal(modalState);
  const ViewIntroductionModalShow = (introId) => {
    setSelectedIntroId(introId);
    setViewIntroductionModal(true);
  };

  const pendingScrollSection = "pending";
  const acceptedScrollSection = "accepted";

  const [userFirstName, setUserFirstName] = useState(null);

  const wantAdId = _.get(getLocalStorage(WANT_AD_KEY), "wantAdId", null);

  useEffect(() => {
    if (!wantAdId) {
      navigate(pageURLs.home);
    }
  }, [wantAdId]);

  /**
   * Shared fetcher for a page of introductions. Throws on API error so React
   * Query surfaces it through the query's error state.
   * @param {function} fetcher service call (pending or accepted)
   * @param {number} page
   * @param {object} extraParams params only one of the two endpoints takes
   */
  const fetchIntroductionsPage = async (fetcher, page, extraParams = {}) => {
    const response = await fetcher({
      wantAdId: wantAdId,
      page: page,
      limit: INTRO_LIST_PAGE_LIMIT,
      ...extraParams,
    });
    if (response?.errorCode !== 0) {
      throw new Error(response?.message || "Failed to fetch introductions");
    }
    return {
      list: _.get(response, "result.List", []) || [],
      count: _.get(response, "result.Count", 0),
      titleText: _.get(response, "result.TitleText", ""),
      page,
    };
  };

  const getNextPageParam = (lastPage, pages) => {
    const loaded = pages.reduce((sum, p) => sum + p.list.length, 0);
    return loaded < lastPage.count ? lastPage.page + 1 : undefined;
  };

  const pendingQuery = useInfiniteQuery({
    queryKey: [PENDING_INTROS_KEY, wantAdId],
    queryFn: ({ pageParam = 1 }) =>
      fetchIntroductionsPage(getIntroductions, pageParam, { isAccept: "0" }),
    getNextPageParam,
    enabled: !!wantAdId,
  });

  const acceptedQuery = useInfiniteQuery({
    queryKey: [ACCEPTED_INTROS_KEY, wantAdId],
    queryFn: ({ pageParam = 1 }) =>
      fetchIntroductionsPage(getAcceptedIntroductions, pageParam),
    getNextPageParam,
    enabled: !!wantAdId,
  });

  const introDetailsQuery = useQuery({
    queryKey: [INTRO_DETAILS_KEY, selectedIntroId],
    enabled: !!selectedIntroId,
    queryFn: async () => {
      const response = await getIntroductionDetaisById(selectedIntroId);
      const responseResult = _.get(response, "result", null);
      if (response?.errorCode !== 0 || !responseResult) {
        throw new Error(
          response?.message || "Failed to fetch introduction details",
        );
      }
      return responseResult;
    },
  });

  const pendingList = useMemo(
    () => (pendingQuery.data?.pages ?? []).flatMap((p) => p.list),
    [pendingQuery.data],
  );
  const acceptedList = useMemo(
    () => (acceptedQuery.data?.pages ?? []).flatMap((p) => p.list),
    [acceptedQuery.data],
  );
  const introDetails = introDetailsQuery.data ?? [];

  // Both sections report the total from the most recently loaded page, the way
  // the manual fetches did.
  const lastPageOf = (query) => {
    const pages = query.data?.pages ?? [];
    return pages.length ? pages[pages.length - 1] : null;
  };
  const pendingListCount = lastPageOf(pendingQuery)?.count ?? 0;
  const acceptedListCount = lastPageOf(acceptedQuery)?.count ?? 0;
  const titleText = lastPageOf(acceptedQuery)?.titleText ?? "";

  // The blocking overlay is driven by the shared loading context, so mirror the
  // queries into it and always release the keys on unmount.
  const listsFetching = pendingQuery.isFetching || acceptedQuery.isFetching;
  const detailsFetching = introDetailsQuery.isFetching;

  useEffect(() => {
    listsFetching
      ? startLoading("introductionsList")
      : stopLoading("introductionsList");
  }, [listsFetching]);

  useEffect(() => {
    detailsFetching ? startLoading("introDetails") : stopLoading("introDetails");
  }, [detailsFetching]);

  useEffect(
    () => () => {
      stopLoading("introductionsList");
      stopLoading("introDetails");
    },
    [],
  );

  const updateIntroPageOnScroll = (section) => {
    if (section === pendingScrollSection) {
      if (pendingQuery.hasNextPage && !pendingQuery.isFetchingNextPage) {
        pendingQuery.fetchNextPage();
      }
    } else if (section === acceptedScrollSection) {
      if (acceptedQuery.hasNextPage && !acceptedQuery.isFetchingNextPage) {
        acceptedQuery.fetchNextPage();
      }
    }
  };

  /**
   * To handle mark not a fit action
   * @param {*} introId
   * @param {*} status
   * @param {*} firstName
   */
  const markNotFitModelShow = (introId, status, firstName = null) => {
    setIntroId(introId);
    setMarkNotFitStatus(status);
    setMarkFitModal(true);
    setUserFirstName(firstName);
  };

  const handleNotFitConfirm = () => {
    markFitModalClose();
    setNotFitReasonPopup(true);
  };

  const updateToNotFit = async (reqPayload, isSkip) => {
    if (reqPayload) {
      startLoading("updateIntroStatus");
      try {
        await updateIntroNotFit(reqPayload)
          .then(async (response) => {
            if (response.errorCode === 0) {
              let data = {
                isdeleted: true,
              };

              let updateresponse = await updateIntroduction(
                reqPayload?.IntroductionId,
                data,
              );
              setNotFitReasonPopup(false);
              toast.success("Mark not a fit was successfully submitted.");
              // Drop the affected list back to its first page, as before.
              const listKey =
                markNotFitStatus === INTRODUCTION_NOT_A_FIT_FROM_PENDING_LIST
                  ? PENDING_INTROS_KEY
                  : ACCEPTED_INTROS_KEY;
              queryClient.resetQueries({ queryKey: [listKey, wantAdId] });
              setIntroId("");
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      } finally {
        stopLoading("updateIntroStatus");
      }
    }
  };

  return (
    <>

      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-0">
              <div className="col-md-12">
                <div className="edit-want-ad-wrap introduction-wrap mw-697">
                  <BackButton onClick={() => {
                      setLocalStorage(WANT_AD_KEY, {
                        ...getLocalStorage(WANT_AD_KEY),
                        isFromHomeFeed: true,
                      });
                      const wantAdId = getLocalStorage(WANT_AD_KEY)?.wantAdId;
                      navigate(`${pageURLs.wantAdDetails}/${wantAdId}`);
                    }} />
                  <h3>
                    All introductions – <span>{titleText}</span>
                  </h3>
                  <div className="payments-wrap intro-content-block">
                    <PendingIntroductionList
                      list={pendingList}
                      count={pendingListCount}
                      updateIntroPageOnScroll={updateIntroPageOnScroll}
                      markNotFitModelShow={(
                        introdctionId,
                        INTRODUCTION_NOT_A_FIT_FROM_PENDING_LIST,
                      ) => {
                        markNotFitModelShow(
                          introdctionId,
                          INTRODUCTION_NOT_A_FIT_FROM_PENDING_LIST,
                        );
                      }}
                      ViewIntroductionModalShow={ViewIntroductionModalShow}
                    />
                    <AcceptedIntroductionList
                      list={acceptedList}
                      count={acceptedListCount}
                      updateIntroPageOnScroll={updateIntroPageOnScroll}
                      markNotFitModelShow={(
                        introdctionId,
                        INTRODUCTION_NOT_A_FIT_FROM_ACCEPTED_LIST,
                        firstName,
                      ) => {
                        markNotFitModelShow(
                          introdctionId,
                          INTRODUCTION_NOT_A_FIT_FROM_ACCEPTED_LIST,
                          firstName,
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      {introDetails.length > 0 && (
        <ViewIntroductionModal
          details={introDetails?.[0]}
          show={showViewIntroductionModal}
          onHide={handleIntroDetailModal}
        />
      )}

      <MarkFitModal
        show={showMarkFitModal}
        onHide={markFitModalClose}
        titleText="Are you sure you want to mark this introduction as not fit?"
        description="This action will end the introduction and associated conversation. Once you delete this it cannot be undone."
        buttonTitle="Confirm"
        updateToNotFit={updateToNotFit}
        introductionId={introductionId}
        showNotFirReasnPopup={showNotFirReasnPopup}
        handleNotFitConfirm={handleNotFitConfirm}
        markFitReasonModalClose={markFitReasonModalClose}
        name={userFirstName}
      />
    </>
  );
};

export default AllIntroductions;
